'use client'

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '@/components/providers/supabase-auth-provider'
import { useSubscription } from '@/lib/hooks/use-subscription'
import { useProfile } from '@/lib/hooks/use-profile'
import { formatTierName, getTierLevel } from '@/lib/tier-helpers'
import { ThemeLogo } from '@/components/theme-logo'
import { useTheme } from 'next-themes'
import { Button } from '@/components/ui/button'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Bell, 
  Menu, 
  X, 
  LogOut, 
  LayoutDashboard, 
  Target, 
  BarChart,
  User as UserIcon,
  Settings,
  CreditCard,
  HelpCircle,
  ChevronDown,
  Info,
  Crown,
  Sparkles,
  Zap,
  BookOpen,
  MessageSquare,
  ChevronRight,
  Search,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  BellOff,
  Lock,
  Lightbulb,
  PenLine,
  Sun,
  Moon
} from 'lucide-react'
import Link from 'next/link'
import { Badge } from '@/components/ui/badge'
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuSubContent
} from '@/components/ui/dropdown-menu'
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { usePathname, useRouter } from 'next/navigation'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { supabase } from '@/lib/supabase'

export function DashboardHeader() {
  const { user, signOut } = useAuth()
  const { profile, isLoading: profileLoading } = useProfile()
  const { tier } = useSubscription()
  const { theme, setTheme, resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  
  useEffect(() => {
    setMounted(true)
  }, [])
  const [hasNewNotifications, setHasNewNotifications] = useState(true)
  const [scrolled, setScrolled] = useState(false)
  const pathname = usePathname()
  const router = useRouter()
  const tierLevel = getTierLevel(tier || 'free')
  
  // User account info state
  const [accountInfo, setAccountInfo] = useState({
    signupDate: new Date().toLocaleDateString(),
    status: 'Active',
    lastLogin: '',
    isLoading: true
  });
  
  // Fetch user account info
  useEffect(() => {
    const fetchAccountInfo = async () => {
      if (!user?.id) return;
      
      try {
        // In production with Supabase, user.created_at should be directly accessible 
        // from the user object returned by the Auth API
        const signupDate = user.created_at 
          ? new Date(user.created_at).toLocaleDateString()
          : new Date().toLocaleDateString();
            
        const lastLogin = user.last_sign_in_at
          ? new Date(user.last_sign_in_at).toLocaleDateString()
          : '';
        
        setAccountInfo({
          signupDate,
          status: 'Active', // Could be determined based on subscription status
          lastLogin,
          isLoading: false
        });
      } catch (error) {
        console.error('Unexpected error fetching account info:', error);
        setAccountInfo(prev => ({ ...prev, isLoading: false }));
      }
    };
    
    fetchAccountInfo();
  }, [user]);
  
  // Update Notification type to include key
  interface Notification {
    id: string; // Used as the notification_key
    type: 'opportunity' | 'system' | 'account';
    title: string;
    message: string;
    date: string;
    createdAt: Date;
    read: boolean;
    icon: any;
    linkUrl?: string;
    dismissible?: boolean;
  }
  
  // Use the new Notification type
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
  
  // Fetch notifications on component mount
  useEffect(() => {
    fetchNotifications();
  }, [user]);

  // Fetch notifications from Supabase, merge with states
  const fetchNotifications = async () => {
    if (!user?.id) {
      setNotifications([]);
      setIsLoadingNotifications(false);
      return;
    }
    
    setIsLoadingNotifications(true);
    try {
      // Array to hold potentially displayable notifications
      let potentialNotifications: Notification[] = [];

      // --- Generate Potential Notifications --- 

      // 1. Welcome Message (Only if not previously dismissed via DB state)
      // NOTE: Using DB state now, removing localStorage check later
      potentialNotifications.push({
        id: 'welcome-message', // Unique Key
        type: 'account' as const,
        title: 'Welcome to GovCon Data!',
        message: 'Explore your dashboard and start tracking opportunities.',
        date: formatNotificationDate(new Date()), 
        createdAt: new Date(),
        read: false, // Will be updated from DB state
        icon: Sparkles,
        linkUrl: '/dashboard/profile',
        dismissible: true
      });

      // 2. Deadline Notifications
      try {
        const today = new Date();
        const fourteenDaysFromNow = new Date(today);
        fourteenDaysFromNow.setDate(today.getDate() + 14);

        const { data: trackedOpsData, error: trackedOpsError } = await supabase
          .from('tracked_opportunities')
          .select('opportunity_id')
          .eq('user_id', user.id);

        if (trackedOpsError) throw trackedOpsError;

        if (trackedOpsData && trackedOpsData.length > 0) {
          const trackedOpportunityIds = trackedOpsData.map(item => item.opportunity_id);
          const { data: opportunitiesData, error: opportunitiesError } = await supabase
            .from('samgov')
            .select('notice_id, title, response_deadline')
            .in('notice_id', trackedOpportunityIds)
            .gte('response_deadline', today.toISOString().split('T')[0])
            .lte('response_deadline', fourteenDaysFromNow.toISOString().split('T')[0])
            .order('response_deadline', { ascending: true });

          if (opportunitiesError) throw opportunitiesError;

          opportunitiesData?.forEach(opp => {
            if (!opp.response_deadline) return;
            const deadline = new Date(opp.response_deadline);
            if (isNaN(deadline.getTime())) return;

            const diffTime = deadline.getTime() - today.getTime();
            const daysUntilDeadline = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            let notificationTitle = '';
            if (daysUntilDeadline <= 3) {
              notificationTitle = `Deadline Fast: ${opp.title} (${daysUntilDeadline}d)`;
            } else if (daysUntilDeadline <= 7) {
              notificationTitle = `Deadline Soon: ${opp.title} (${daysUntilDeadline}d)`;
            } else if (daysUntilDeadline <= 14) {
              notificationTitle = `Deadline Approaching: ${opp.title} (${daysUntilDeadline}d)`;
            }

            if (notificationTitle) {
              potentialNotifications.push({
                id: `deadline-${opp.notice_id}`, // Unique Key
                type: 'opportunity' as const,
                title: notificationTitle,
                message: `Due: ${deadline.toLocaleDateString()}`,
                date: formatNotificationDate(new Date()),
                createdAt: new Date(),
                read: false, // Will be updated from DB state
                icon: Clock,
                linkUrl: `/dashboard?opportunity_id=${opp.notice_id}`,
                dismissible: true
              });
            }
          });
        }

        // Check SLED opportunities for deadlines
        if (trackedOpsData && trackedOpsData.length > 0) {
          const trackedOpportunityIds = trackedOpsData.map(item => item.opportunity_id);
          // SLED opportunity IDs are numeric strings
          const sledIds = trackedOpportunityIds.filter(id => /^\d+$/.test(String(id)));
          
          if (sledIds.length > 0) {
            const { data: sledOpportunitiesData, error: sledError } = await supabase
              .from('unified_sled_opportunities')
              .select('id, title, due_date')
              .in('id', sledIds.map(id => parseInt(id)))
              .gte('due_date', today.toISOString().split('T')[0])
              .lte('due_date', fourteenDaysFromNow.toISOString().split('T')[0])
              .order('due_date', { ascending: true });

            if (!sledError && sledOpportunitiesData) {
              sledOpportunitiesData.forEach(opp => {
                if (!opp.due_date) return;
                const deadline = new Date(opp.due_date);
                if (isNaN(deadline.getTime())) return;

                const diffTime = deadline.getTime() - today.getTime();
                const daysUntilDeadline = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                let notificationTitle = '';
                if (daysUntilDeadline <= 3) {
                  notificationTitle = `Deadline Fast: ${opp.title} (${daysUntilDeadline}d)`;
                } else if (daysUntilDeadline <= 7) {
                  notificationTitle = `Deadline Soon: ${opp.title} (${daysUntilDeadline}d)`;
                } else if (daysUntilDeadline <= 14) {
                  notificationTitle = `Deadline Approaching: ${opp.title} (${daysUntilDeadline}d)`;
                }

                if (notificationTitle) {
                  potentialNotifications.push({
                    id: `deadline-sled-${opp.id}`, // Unique Key for SLED
                    type: 'opportunity' as const,
                    title: notificationTitle,
                    message: `Due: ${deadline.toLocaleDateString()}`,
                    date: formatNotificationDate(new Date()),
                    createdAt: new Date(),
                    read: false, // Will be updated from DB state
                    icon: Clock,
                    linkUrl: `/dashboard?opportunity_id=${opp.id}`,
                    dismissible: true
                  });
                }
              });
            }
          }
        }
      } catch (error) {
        console.error('Error fetching deadline notifications:', error);
        potentialNotifications.push({
          id: 'system-deadline-error',
          type: 'system' as const,
          title: 'Could not check deadlines',
          message: 'Issue checking opportunity deadlines.',
          date: formatNotificationDate(new Date()),
          createdAt: new Date(),
          read: false,
          icon: AlertCircle,
          dismissible: true
        });
      }
      
      // 3. Bid Update Notifications
      try {
        const { data: bidsWithUpdates, error: updateError } = await supabase
          .from('tracked_opportunities')
          .select('opportunity_id, update_detected_at, update_details')
          .eq('user_id', user.id)
          .eq('has_updates', true);

        if (!updateError && bidsWithUpdates) {
          for (const bid of bidsWithUpdates) {
            // Get the opportunity title
            let title = 'Tracked Bid';
            const isSled = /^\d+$/.test(String(bid.opportunity_id));
            
            if (isSled) {
              const { data: sledOpp } = await supabase
                .from('unified_sled_opportunities')
                .select('title')
                .eq('id', parseInt(bid.opportunity_id))
                .single();
              if (sledOpp?.title) {
                title = sledOpp.title.substring(0, 40) + (sledOpp.title.length > 40 ? '...' : '');
              }
            } else {
              const { data: fedOpp } = await supabase
                .from('samgov')
                .select('title')
                .eq('notice_id', bid.opportunity_id)
                .single();
              if (fedOpp?.title) {
                title = fedOpp.title.substring(0, 40) + (fedOpp.title.length > 40 ? '...' : '');
              }
            }

            // Build message based on what changed
            let message = 'This bid has been updated.';
            const details = bid.update_details as any;
            if (details) {
              const changes: string[] = [];
              if (details.deadline_changed) changes.push('deadline changed');
              if (details.documents_added) changes.push(`${details.documents_added} new doc${details.documents_added > 1 ? 's' : ''}`);
              if (details.documents_removed) changes.push(`${details.documents_removed} doc${details.documents_removed > 1 ? 's' : ''} removed`);
              if (details.amendment_detected && changes.length === 0) changes.push('bid amended');
              if (changes.length > 0) {
                message = changes.join(', ').charAt(0).toUpperCase() + changes.join(', ').slice(1);
              }
            }

            potentialNotifications.push({
              id: `bid-update-${bid.opportunity_id}`,
              type: 'opportunity' as const,
              title: `Bid Updated: ${title}`,
              message: message,
              date: bid.update_detected_at ? formatNotificationDate(new Date(bid.update_detected_at)) : formatNotificationDate(new Date()),
              createdAt: bid.update_detected_at ? new Date(bid.update_detected_at) : new Date(),
              read: false,
              icon: Bell,
              linkUrl: `/dashboard?opportunity_id=${bid.opportunity_id}`,
              dismissible: true
            });
          }
        }
      } catch (error) {
        console.error('Error fetching bid update notifications:', error);
      }

      // 4. New Match Notifications (written by send-notifications edge function)
      try {
        const { data: newMatchStates } = await supabase
          .from('user_notification_states')
          .select('notification_key, is_dismissed')
          .eq('user_id', user.id)
          .eq('is_read', false)
          .eq('is_dismissed', false)
          .like('notification_key', 'new-match-%')
          .limit(10);

        if (newMatchStates && newMatchStates.length > 0) {
          // Extract opp IDs — keys are either new-match-<notice_id> (string) or new-match-<int> (SLED)
          const oppIds = newMatchStates.map(s => s.notification_key.replace('new-match-', ''));
          const sledIds = oppIds.filter(id => /^\d+$/.test(id)).map(Number);
          const fedIds = oppIds.filter(id => !/^\d+$/.test(id));

          const titlesMap: Record<string, string> = {};

          if (fedIds.length > 0) {
            const { data: fedOpps } = await supabase
              .from('samgov')
              .select('notice_id, title')
              .in('notice_id', fedIds);
            (fedOpps || []).forEach((o: any) => { titlesMap[o.notice_id] = o.title; });
          }
          if (sledIds.length > 0) {
            const { data: sledOpps } = await supabase
              .from('unified_sled_opportunities')
              .select('id, title')
              .in('id', sledIds);
            (sledOpps || []).forEach((o: any) => { titlesMap[String(o.id)] = o.title; });
          }

          for (const state of newMatchStates) {
            const oppId = state.notification_key.replace('new-match-', '');
            const title = titlesMap[oppId];
            if (!title) continue;
            potentialNotifications.push({
              id: state.notification_key,
              type: 'opportunity' as const,
              title: `New Match: ${title.substring(0, 45)}${title.length > 45 ? '...' : ''}`,
              message: 'A new opportunity matches your profile.',
              date: formatNotificationDate(new Date()),
              createdAt: new Date(),
              read: false,
              icon: Sparkles,
              linkUrl: '/dashboard',
              dismissible: true,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching new match notifications:', error);
      }

      // 5. Other System/Account Notifications (Example: Upgrade Prompt)
      // Note: These should also have unique, stable IDs
      if (tier !== 'professional') {
         potentialNotifications.push({
           id: 'system-upgrade-prompt', // Unique Key
           type: 'system' as const,
           title: 'Unlock Full Potential',
           message: 'Upgrade to Professional to access all opportunities, AI features, and bid management.',
           date: formatNotificationDate(new Date()),
           createdAt: new Date(),
           read: false, // Will be updated from DB state
           icon: Crown,
           dismissible: true,
           linkUrl: '/pricing'
         });
      }

      // --- Fetch Existing Notification States from DB --- 
      const notificationKeys = potentialNotifications.map(n => n.id);
      let notificationStates: Record<string, { is_read: boolean; is_dismissed: boolean }> = {};
      let rowsToUpsert: { user_id: string; notification_key: string; is_read: boolean; is_dismissed: boolean }[] = [];

      if (notificationKeys.length > 0) {
        const { data: stateData, error: stateError } = await supabase
          .from('user_notification_states')
          .select('notification_key, is_read, is_dismissed')
          .eq('user_id', user.id)
          .in('notification_key', notificationKeys);

        if (stateError) {
          console.error("Error fetching notification states:", stateError);
          // Proceed without state data, notifications will appear as new/undismissed
        } else if (stateData) {
          stateData.forEach(state => {
            notificationStates[state.notification_key] = { 
              is_read: state.is_read,
              is_dismissed: state.is_dismissed
            };
          });
        }
      }

      // --- Filter, Merge, and Prepare Final List --- 
      const finalNotifications: Notification[] = [];
      potentialNotifications.forEach(notification => {
        const state = notificationStates[notification.id];
        
        if (state) {
          // State exists, check if dismissed
          if (state.is_dismissed) {
            return; // Skip dismissed notification
          }
          // Apply read status from state
          notification.read = state.is_read;
        } else {
          // State doesn't exist, treat as new (unread, not dismissed)
          // Mark this state to be created in the DB
          rowsToUpsert.push({
             user_id: user.id, 
             notification_key: notification.id, 
             is_read: false, 
             is_dismissed: false
          });
          notification.read = false; // Ensure it's marked as unread locally
        }
        
        finalNotifications.push(notification);
      });
      
      // --- Upsert Default States for New Notifications (Optional but recommended) ---
      if (rowsToUpsert.length > 0) {
          const { error: upsertError } = await supabase
              .from('user_notification_states')
              .upsert(rowsToUpsert, { onConflict: 'user_id, notification_key', ignoreDuplicates: true }); // Use ignoreDuplicates
              
          if (upsertError) {
              console.error("Error upserting default notification states:", upsertError);
          }
      }

      // Sort notifications (e.g., by createdAt date, or keep generated order)
      finalNotifications.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setNotifications(finalNotifications);
      
    } catch (error) {
      console.error('Error processing notifications:', error);
      // Optionally set an error state or display a generic error notification
      setNotifications([]); // Clear notifications on major error
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  // Format notification date for display
  const formatNotificationDate = (date: Date): string => {
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };
  
  // Check for unread notifications
  useEffect(() => {
    const unreadNotifications = notifications.some(notification => !notification.read);
    setHasNewNotifications(unreadNotifications);
  }, [notifications]);
  
  // Mark notification as read
  const markAsRead = async (id: string) => {
    // Optimistically update local state
    setNotifications(prevNotifications => 
      prevNotifications.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
    
    // Update database
    if (!user?.id) return;
    try {
      const { error } = await supabase
        .from('user_notification_states')
        .upsert(
           { user_id: user.id, notification_key: id, is_read: true, is_dismissed: false }, 
           { onConflict: 'user_id, notification_key' }
         )
        .select(); // Must select to get upsert error if RLS fails update
        
      if (error) throw error;
      
    } catch (err) {
      console.error("Error marking notification as read in DB:", err);
      // Optionally revert local state or show error to user
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => 
          notification.id === id ? { ...notification, read: false } : notification // Revert read state
        )
      );
    }
  };
  
  // Dismiss notification
  const dismissNotification = async (id: string) => {
    // Optimistically remove from local state
    const notificationToRemove = notifications.find(n => n.id === id);
    setNotifications(prevNotifications => 
      prevNotifications.filter(notification => notification.id !== id)
    );
    
    // Update database
    if (!user?.id) return;
    try {
      const { error } = await supabase
        .from('user_notification_states')
        .upsert(
           { user_id: user.id, notification_key: id, is_dismissed: true }, 
           { onConflict: 'user_id, notification_key' } 
         )
        .select(); // Must select
        
      if (error) throw error;
      
      // Remove localStorage check for welcome message if it exists
      if (id === 'welcome-message') {
         localStorage.removeItem('welcomeNotificationDismissed');
      }
      
    } catch (err) {
      console.error("Error dismissing notification in DB:", err);
      // Re-add notification to local state if DB update failed
      if (notificationToRemove) {
        setNotifications(prev => [notificationToRemove, ...prev].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
      }
    }
  };
  
  // Mark all notifications as read
  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length === 0 || !user?.id) return;
    
    // Optimistically update local state
    setNotifications(prevNotifications => 
      prevNotifications.map(notification => ({ ...notification, read: true }))
    );
    
    // Prepare data for DB update
    const updates = unreadNotifications.map(n => ({
      user_id: user.id,
      notification_key: n.id,
      is_read: true,
      is_dismissed: false // Ensure we don't accidentally dismiss
    }));
    
    // Update database
    try {
      const { error } = await supabase
        .from('user_notification_states')
        .upsert(updates, { onConflict: 'user_id, notification_key' });
        
      if (error) throw error;
      
    } catch (err) {
      console.error("Error marking all as read in DB:", err);
      // Revert local state on error
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => {
          const wasUnread = unreadNotifications.some(un => un.id === notification.id);
          return wasUnread ? { ...notification, read: false } : notification;
        })
      );
    }
  };
  
  // Handle notification click
  const handleNotificationClick = (notification: typeof notifications[0]) => {
    markAsRead(notification.id);
    
    // Navigate to the relevant page if a linkUrl is provided
    if (notification.linkUrl) {
      router.push(notification.linkUrl);
    }
  };
  
  // Filter notifications by type
  const opportunityNotifications = notifications.filter(notification => notification.type === 'opportunity');
  const systemNotifications = notifications.filter(notification => notification.type === 'system');
  const accountNotifications = notifications.filter(notification => notification.type === 'account');
  
  // Count unread notifications
  const unreadCount = notifications.filter(notification => !notification.read).length;
  
  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);
  
  const navItems = [
    {
      title: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      description: 'View your personalized dashboard'
    },
    {
      title: 'Active Opportunities',
      href: '/dashboard/opportunities',
      icon: Target,
      description: 'Explore contracts and opportunities'
    },
  ]
  
  // Different features available based on subscription tier
  const tierFeatures = {
    professional: [
      { name: 'AI Insights', icon: Sparkles },
      { name: 'Priority Support', icon: MessageSquare },
      { name: 'Custom Analytics', icon: BarChart }
    ],
    free: [
      { name: 'Basic Search', icon: Search },
      { name: 'Limited Opportunities', icon: Target }
    ],
  }

  // Get features for the current tier
  const currentTierFeatures =
    tier === 'professional' ? tierFeatures.professional :
    tierFeatures.free;
  
  // Animation variants
  const navItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      // The redirect is handled in the auth provider
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const tierDisplay = formatTierName(tier || 'free')
  const userInitials = user?.email ? user.email[0].toUpperCase() : 'U'
  
  // Add loading state based on profile loading
  if (profileLoading || accountInfo.isLoading || isLoadingNotifications) {
    // Render a minimal loading state for the header
    return (
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-20 items-center max-w-screen-2xl mx-auto">
          <div className="flex items-center pl-4 md:pl-8">
            <ThemeLogo size="medium" increasedSize={false} variant="words" />
          </div>
          <div className="flex-grow"></div>
          <div className="flex items-center space-x-2 animate-pulse pr-4 md:pr-8">
             <div className="h-9 w-9 rounded-md bg-muted"></div>
             <div className="h-9 w-9 rounded-md bg-muted"></div>
             <div className="h-9 w-9 rounded-full bg-muted"></div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
    <header className={cn(
      "sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
      scrolled 
        ? "shadow-sm" 
        : ""
    )}>
      <motion.div 
        className="container flex h-20 items-center max-w-screen-2xl mx-auto"
        initial={{ y: -10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-center">
          <Link href="/dashboard" className="mr-6 flex items-center group pl-4 md:pl-8">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              className="flex items-center"
            >
              <ThemeLogo size="medium" increasedSize={false} variant="words" linkDisabled />
            </motion.div>
          </Link>
        </div>

        {/* Desktop Navigation (Moved back after logo) */}
        <div className="hidden md:flex flex-1 items-center justify-center">
          <motion.ul className="flex items-center space-x-1 lg:space-x-2">
            {navItems.map((item, i) => {
              // Refined isActive logic for desktop
              const isActive = item.href === '/dashboard' 
                  ? pathname === item.href 
                  : pathname?.startsWith(item.href);

              return (
                <motion.li 
                   key={item.title} 
                   variants={navItemVariants} 
                   initial="hidden" 
                   animate="visible" 
                   transition={{ delay: i * 0.1 }}
                   className="relative"
                 >
                   <HoverCard openDelay={100} closeDelay={100}>
                      <HoverCardTrigger asChild>
                         <Link
                           href={item.href}
                           className={cn(
                             "flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors",
                             isActive
                               ? "bg-primary/10 text-primary"
                               : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                           )}
                         >
                           {item.icon && <item.icon className="mr-1.5 h-4 w-4 flex-shrink-0" />}
                           <span className="whitespace-nowrap">{item.title}</span>
                         </Link>
                       </HoverCardTrigger>
                       <HoverCardContent className="w-60 text-sm" sideOffset={10}>
                         {item.description}
                       </HoverCardContent>
                    </HoverCard>
                  {/* Optional: Add active indicator if desired */}
                  {/* isActive && (
                    <motion.div 
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" 
                      layoutId="active-nav-indicator" // Animate indicator position
                      initial={false}
                      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    />
                  ) */}
                 </motion.li>
              )
            })}

            {/* --- Proposal Nav Item --- */}
            <motion.li variants={navItemVariants} initial="hidden" animate="visible" transition={{ delay: (navItems.length) * 0.1 }} className="relative">
              <HoverCard openDelay={100} closeDelay={100}>
                <HoverCardTrigger asChild>
                  {tierLevel < 1 ? (
                    <span
                      className="flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors text-muted-foreground opacity-50 cursor-not-allowed"
                    >
                      <PenLine className="mr-1.5 h-4 w-4 flex-shrink-0" />
                      <span className="whitespace-nowrap">Proposal</span>
                      <Lock className="ml-1.5 h-3.5 w-3.5" />
                    </span>
                  ) : (
                    <Link
                      href="/ai-proposal-generator"
                      className={cn(
                        "flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors",
                        pathname === '/ai-proposal-generator'
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      )}
                    >
                      <PenLine className="mr-1.5 h-4 w-4 flex-shrink-0" />
                      <span className="whitespace-nowrap">Proposal</span>
                    </Link>
                  )}
                </HoverCardTrigger>
                <HoverCardContent className="w-60 text-sm" sideOffset={10}>
                  {tierLevel < 1 ? 'Requires Professional plan' : 'AI-powered proposal generation and writing assistance'}
                </HoverCardContent>
              </HoverCard>
            </motion.li>



          </motion.ul>
        </div>

        {/* Mobile menu toggle */}
        <Button 
          variant="ghost" 
          className="md:hidden ml-auto mr-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <motion.div
            animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </motion.div>
          <span className="sr-only">Toggle menu</span>
        </Button>

        {/* Right side items */}
        <div className="hidden md:flex items-center space-x-1 lg:space-x-2 pr-4 md:pr-8">
          {/* Help button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 rounded-md relative group overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-tr from-primary/0 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <HelpCircle className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="font-normal">
                Help & Resources
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/resources/guides" className="flex items-center cursor-pointer">
                  <BookOpen className="mr-2 h-4 w-4" />
                  Guides
                  <div className="ml-auto">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/resources/faq" className="flex items-center cursor-pointer">
                  <HelpCircle className="mr-2 h-4 w-4" />
                  FAQ
                  <div className="ml-auto">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/documentation" className="flex items-center cursor-pointer">
                  <FileText className="mr-2 h-4 w-4" />
                  Documentation
                  <div className="ml-auto">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-9 w-9 rounded-md relative group overflow-visible"
                >
                  <span className="absolute inset-0 bg-gradient-to-tr from-primary/0 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <Bell className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center border-2 border-background">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 max-h-[70vh] overflow-y-auto">
              <div className="flex items-center justify-between px-4 py-2">
                <h3 className="font-semibold text-sm">Notifications</h3>
                {unreadCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 text-xs"
                    onClick={markAllAsRead}
                  >
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    Mark all as read
                  </Button>
                )}
              </div>
              
              <DropdownMenuSeparator />
              
              {isLoadingNotifications ? (
                <div className="py-8 text-center">
                  <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin mx-auto mb-3"></div>
                  <p className="text-sm text-muted-foreground">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="py-8 text-center">
                  <BellOff className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
                  <p className="text-sm text-muted-foreground">No notifications</p>
                </div>
              ) : (
                <>
                  {opportunityNotifications.length > 0 && (
                    <>
                      <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">
                        Opportunities
                      </DropdownMenuLabel>
                      {opportunityNotifications.map((notification) => (
                        <DropdownMenuItem 
                          key={notification.id}
                          className={cn(
                            "p-3 cursor-pointer relative group/item",
                            !notification.read && "bg-primary/5"
                          )}
                          onSelect={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <div 
                            className="flex gap-3 w-full" 
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className={cn(
                              "flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center",
                              !notification.read ? "bg-primary/10" : "bg-muted"
                            )}>
                              <notification.icon className={cn(
                                "h-4 w-4",
                                !notification.read ? "text-primary" : "text-muted-foreground"
                              )} />
                            </div>
                            <div className="flex-1 space-y-1">
                              <p className={cn(
                                "text-sm leading-none",
                                !notification.read && "font-medium"
                              )}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-muted-foreground">{notification.message}</p>
                              <p className="text-xs text-muted-foreground/70">{notification.date}</p>
                            </div>
                            {!notification.read && (
                              <div className="h-2 w-2 rounded-full bg-primary self-start mt-1.5" />
                            )}
                          </div>
                          {notification.dismissible && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-6 w-6 rounded-full opacity-0 group-hover/item:opacity-100 hover:bg-muted/50"
                              onClick={(e) => {
                                e.stopPropagation();
                                dismissNotification(notification.id);
                              }}
                            >
                              <X className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="sr-only">Dismiss notification</span>
                            </Button>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                  
                  {systemNotifications.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">
                        System
                      </DropdownMenuLabel>
                      {systemNotifications.map((notification) => (
                        <DropdownMenuItem 
                          key={notification.id}
                          className={cn(
                            "p-3 cursor-pointer relative group/item",
                            !notification.read && "bg-primary/5"
                          )}
                          onSelect={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <div 
                            className="flex gap-3 w-full" 
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className={cn(
                              "flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center",
                              !notification.read ? "bg-primary/10" : "bg-muted"
                            )}>
                              <notification.icon className={cn(
                                "h-4 w-4",
                                !notification.read ? "text-primary" : "text-muted-foreground"
                              )} />
                            </div>
                            <div className="flex-1 space-y-1">
                              <p className={cn(
                                "text-sm leading-none",
                                !notification.read && "font-medium"
                              )}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-muted-foreground">{notification.message}</p>
                              <p className="text-xs text-muted-foreground/70">{notification.date}</p>
                            </div>
                            {!notification.read && (
                              <div className="h-2 w-2 rounded-full bg-primary self-start mt-1.5" />
                            )}
                          </div>
                          {notification.dismissible && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-6 w-6 rounded-full opacity-0 group-hover/item:opacity-100 hover:bg-muted/50"
                              onClick={(e) => {
                                e.stopPropagation();
                                dismissNotification(notification.id);
                              }}
                            >
                              <X className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="sr-only">Dismiss notification</span>
                            </Button>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                  
                  {accountNotifications.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">
                        Account
                      </DropdownMenuLabel>
                      {accountNotifications.map((notification) => (
                        <DropdownMenuItem 
                          key={notification.id}
                          className={cn(
                            "p-3 cursor-pointer relative group/item",
                            !notification.read && "bg-primary/5"
                          )}
                          onSelect={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <div 
                            className="flex gap-3 w-full" 
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className={cn(
                              "flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center",
                              !notification.read ? "bg-primary/10" : "bg-muted"
                            )}>
                              <notification.icon className={cn(
                                "h-4 w-4",
                                !notification.read ? "text-primary" : "text-muted-foreground"
                              )} />
                            </div>
                            <div className="flex-1 space-y-1">
                              <p className={cn(
                                "text-sm leading-none",
                                !notification.read && "font-medium"
                              )}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-muted-foreground">{notification.message}</p>
                              <p className="text-xs text-muted-foreground/70">{notification.date}</p>
                            </div>
                            {!notification.read && (
                              <div className="h-2 w-2 rounded-full bg-primary self-start mt-1.5" />
                            )}
                          </div>
                          {notification.dismissible && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 h-6 w-6 rounded-full opacity-0 group-hover/item:opacity-100 hover:bg-muted/50"
                              onClick={(e) => {
                                e.stopPropagation();
                                dismissNotification(notification.id);
                              }}
                            >
                              <X className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="sr-only">Dismiss notification</span>
                            </Button>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full h-9 w-9 p-0 ml-1 border-2 overflow-hidden transition-all hover:border-primary hover:scale-105 group"
              >
                <Avatar className="h-full w-full">
                  <AvatarImage 
                    src={profile?.company_logo_url ? `${profile.company_logo_url}?t=${Date.now()}` : ''}
                    alt={profile?.full_name || user?.email || 'User'} 
                  />
                  <AvatarFallback className="bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                    {profile?.full_name?.[0].toUpperCase() || user?.email?.[0].toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{profile?.full_name || user?.email}</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem asChild>
                  <Link href="/dashboard/settings" className="flex items-center cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                    <div className="ml-auto">
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </Link>
                </DropdownMenuItem>
                {tier !== 'professional' && (
                  <DropdownMenuItem asChild>
                    <Link href="/pricing" className="flex items-center cursor-pointer">
                      <CreditCard className="mr-2 h-4 w-4" />
                      Upgrade Now
                      <div className="ml-auto">
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </Link>
                  </DropdownMenuItem>
                )}
              </DropdownMenuGroup>
              
              <DropdownMenuSeparator />
              
              {/* Theme toggle in dropdown */}
              <DropdownMenuItem
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                className="flex items-center cursor-pointer"
              >
                {theme === 'light' ? (
                  <Moon className="mr-2 h-4 w-4" />
                ) : (
                  <Sun className="mr-2 h-4 w-4" />
                )}
                <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleSignOut}
                className="text-red-600 dark:text-red-400 focus:text-red-700 dark:focus:text-red-300"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

      </motion.div>
    </header>
    
    {/* Mobile menu - rendered via portal outside header to avoid clipping */}
    {mounted && isMobileMenuOpen && typeof window !== 'undefined' && createPortal(
      <div 
        className="fixed inset-x-0 top-20 bottom-0 z-[100] border-t border-border md:hidden overflow-y-auto"
        style={{ 
          backgroundColor: resolvedTheme === 'dark' 
            ? 'hsl(215, 25%, 4%)' 
            : 'hsl(215, 25%, 97%)',
          opacity: 1,
        }}
      >
        <div 
          className="container p-6"
          style={{ 
            backgroundColor: resolvedTheme === 'dark' 
              ? 'hsl(215, 25%, 4%)' 
              : 'hsl(215, 25%, 97%)',
          }}
        >
          <nav className="flex flex-col space-y-6">
                {navItems.map((item, i) => {
                  // Refined isActive logic for mobile
                  const isActive = item.href === '/dashboard' 
                      ? pathname === item.href 
                      : pathname?.startsWith(item.href);
                  
                  return (
                    <div key={item.title}>
                      <Link
                        href={item.href}
                        className={cn(
                          "flex items-center text-base font-medium p-2 rounded-md text-foreground", 
                          isActive 
                            ? "bg-primary/10 text-primary" 
                            : "hover:bg-muted/50 hover:text-foreground"
                        )}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.icon && <item.icon className="mr-2 h-5 w-5" />}
                        {item.title}
                      </Link>
                    </div>
                  );
                })}
                
                {/* Proposal - Mobile */}
                <div>
                  <Link
                    href="/ai-proposal-generator"
                    className={cn(
                      "flex items-center text-base font-medium p-2 rounded-md text-foreground",
                      pathname === '/ai-proposal-generator'
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted/50 hover:text-foreground",
                      tierLevel < 1 && "opacity-50 pointer-events-none"
                    )}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <PenLine className="mr-2 h-5 w-5" />
                    Proposal
                    {tierLevel < 1 && <Lock className="ml-auto h-4 w-4" />}
                  </Link>
                </div>

                  
                  {/* Quick feature access for mobile - Removed as per request */}
                  
                  <div className="border-t pt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <UserIcon className="mr-2 h-5 w-5 text-foreground" />
                        <span className="font-medium text-foreground">{user?.email}</span>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "uppercase h-7 gap-1.5 px-2.5 font-semibold text-xs tracking-wide",
                          tier === 'professional' ?
                            "bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-950/40 dark:to-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900/50 shadow-sm shadow-amber-200/50 dark:shadow-amber-900/20" :
                            "bg-primary/5 dark:bg-primary/10 text-muted-foreground",
                        )}
                      >
                        {tier === 'professional' ? (
                          <Crown className="h-3 w-3 text-amber-500" />
                        ) : null}
                        {tierDisplay}
                      </Badge>
                    </div>
                    <Link href="/dashboard/settings" className="flex items-center text-base text-foreground hover:text-primary hover:bg-muted/50 p-2 rounded-md" onClick={() => setIsMobileMenuOpen(false)}>
                      <Settings className="mr-2 h-5 w-5 text-foreground" />
                      Settings
                    </Link>
                    {tier !== 'professional' && (
                      <Link href="/pricing" className="flex items-center text-base text-foreground hover:text-primary hover:bg-muted/50 p-2 rounded-md" onClick={() => setIsMobileMenuOpen(false)}>
                        <CreditCard className="mr-2 h-5 w-5 text-foreground" />
                        Upgrade Now
                      </Link>
                    )}
                    <Button
                      onClick={handleSignOut}
                      variant="destructive"
                      className="w-full"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out
                    </Button>
                </div>
          </nav>
        </div>
      </div>,
      document.body
    )}
  </>
  )
} 